import { useEffect } from 'react';
import { connect } from "react-redux";
import PropTypes from 'prop-types';
import { Row, Col, Typography, Table, Spin, Progress, Tabs } from 'antd';
import { DatabaseOutlined } from '@ant-design/icons';
import systemActions from '../../redux/systemInfos/actions';
import { getFilteredBackups } from '../../redux/systemInfos/selectors';
import IntlMessages from '../../components/utility/intlMessages';
import Box from '../../components/utility/box';
import moment from 'moment';

const { Title } = Typography;
const { TabPane } = Tabs;

const { getBackups, setBackupsFilter } = systemActions;

function Backups(props) {
  useEffect(() => {
    props.getBackups();

    return () => {
      props.setBackupsFilter('');
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getProgress = (percentage) => {
    return <Progress type={'circle'} percent={parseInt(percentage, 10)} width={40} />
  }

  const getResourceTypeIcon = (resourceType) => {
    return resourceType && typeof resourceType === 'string' &&
      resourceType.toUpperCase() === "RDS"
      ? <DatabaseOutlined size={25} />
      : resourceType;
  }

  const columns = [
    {
      key: "resource",
      title: <IntlMessages id="backups.resource" />,
      dataIndex: 'resource',
      sorter: (a, b) => a.resource.localeCompare(b.resource),
      render: (text, record) => (
        <span>
          {text} {getResourceTypeIcon(record.resourceType)}
        </span>
      ),
    },
    {
      key: "state",
      title: <IntlMessages id="backups.state" />,
      dataIndex: 'state',
      sorter: (a, b) => a.state.localeCompare(b.state)
    },
    {
      key: "startedAt",
      title: <IntlMessages id="backups.startedAt" />,
      dataIndex: 'startedAt',
      sorter: (a, b) => moment(a.startedAt).diff(b.startedAt),
    },
    {
      key: "percentage",
      title: <IntlMessages id="backups.percentage" />,
      dataIndex: 'percentage',
      sorter: (a, b) => a.percentage - b.percentage,
      render: (text) => (
        <span>
          {getProgress(text)}
        </span>
      ),
    },
    {
      key: "awsLink",
      title: <IntlMessages id="backups.awsLink" />,
      dataIndex: 'awsLink',
      render: (text) => (
        <span>
          <a href={text} target="_blank" rel="noreferrer">Aws Link</a>
        </span>
      ),
    },
  ];

  function handleResourceFilter(key) {
    props.setBackupsFilter(key);
  }

  const tableColumns = columns.map(item => ({ ...item, ellipsis: true }));
  return (
    <Spin spinning={props.loading > 0}>
      <Box>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Title level={4} style={{ textAlign: "center" }}>
              <IntlMessages id="backups.title" />
            </Title>

            <Tabs defaultActiveKey="all" onChange={handleResourceFilter}>
              <TabPane tab={<IntlMessages id="general.all" />} key="all" />
              <TabPane tab={<IntlMessages id="backup.personal" />} key="personal" />
              <TabPane tab={<IntlMessages id="backup.environment" />} key="environment" />
              <TabPane tab={<IntlMessages id="general.other" />} key="other" />
              <TabPane tab={<IntlMessages id="backup.medical" />} key="medical" />
              <TabPane tab={<IntlMessages id="backup.eprescription" />} key="eprescription" />
              <TabPane tab={<IntlMessages id="backup.dicomServer" />} key="dicomServer" />
              <TabPane tab={<IntlMessages id="backup.dicomIndex" />} key="dicomIndex" />
              <TabPane tab={<IntlMessages id="backup.mongo" />} key="mongo" />
            </Tabs>

            <Table
              bordered={true}
              showHeader
              pagination={{ position: 'bottom' }}
              columns={tableColumns}
              dataSource={props.data}
              rowKey={(record) => record.awsLink}
            />
          </Col>
        </Row>
      </Box>
    </Spin>
  )
}

Backups.propTypes = {
  data: PropTypes.array.isRequired,
  loading: PropTypes.number.isRequired,

  getBackups: PropTypes.func.isRequired,
  setBackupsFilter: PropTypes.func.isRequired,
};

Backups.defaultProps = {
  data: [],
}

const mapActionsToProps = {
  getBackups,
  setBackupsFilter
};

function mapStateToProps(state) {
  return {
    loading: state.SystemInfos.get('loading'),
    data: getFilteredBackups(state)
  };
}

const connectedBackups = connect(mapStateToProps, mapActionsToProps)(Backups);
export default connectedBackups;